import {
  CYCLE_TASK_DEPENDENCY,
  DEPENDENCY_PICKER_DISPLAY_KEYS,
  SETUP_TEMPLATE_DEPENDENCY,
} from '../../../../utils/constants.js';

/**
 * @param {*} id
 * @returns {string}
 */
export function templateIdString(id) {
  if (id == null || id === '') return '';
  if (typeof id === 'object') {
    const raw = id._id ?? id.$oid ?? id.id;
    if (raw != null) return String(raw);
  }
  return String(id);
}

/**
 * Stable id for dependency picker rows (API shapes vary).
 * @param {object} row
 * @returns {string}
 */
export function pickerRowId(row = {}) {
  return templateIdString(row._id ?? row.id ?? row.taskId ?? row.task_id);
}

/**
 * @param {Array<{ taskId?: string }>} chips
 * @returns {Set<string>}
 */
export function buildLinkedTaskIdSet(chips = []) {
  const ids = new Set();
  (chips || []).forEach((chip) => {
    const id = templateIdString(chip.taskId);
    if (id) ids.add(id);
  });
  return ids;
}

/**
 * @param {object} row
 * @param {Set<string>|null|undefined} linkedTaskIds
 * @returns {boolean}
 */
export function isPickerRowLinked(row = {}, linkedTaskIds = null) {
  const id = pickerRowId(row);
  if (!id || !linkedTaskIds) return false;
  return linkedTaskIds.has(id);
}

/**
 * @param {number|string} duedays
 * @returns {boolean}
 */
export function hasSubjectDuedays(duedays) {
  if (duedays === '' || duedays == null) return false;
  return Number.isFinite(Number(duedays));
}

/**
 * @param {Array<{ linkType?: string }>} chips
 * @returns {string}
 */
export function dependencySummaryLabel(chips) {
  const list = Array.isArray(chips) ? chips : [];
  if (!list.length) return '';
  let blockingCount = 0;
  let relatedCount = 0;
  let waitingCount = 0;
  list.forEach((c) => {
    const t = String(c.linkType || '').toLowerCase();
    if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED) {
      relatedCount += 1;
    } else if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS) {
      blockingCount += 1;
    } else if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKING) {
      waitingCount += 1;
    }
  });
  const summary = `${list.length} linked · ${blockingCount} blocking, ${relatedCount} related`;
  if (waitingCount > 0) return `${summary}, ${waitingCount} waiting on`;
  return summary;
}

/** Same cutoff the dependency table uses before the ellipsis. */
export const DEPENDENCY_DESCRIPTION_MAX = 40;

/**
 * @param {string|null|undefined} value
 * @param {number} [max]
 * @returns {string}
 */
export function truncateDependencyDescription(value, max = DEPENDENCY_DESCRIPTION_MAX) {
  const text = value != null ? String(value).trim() : '';
  if (text.length <= max) return text;
  return `${text.slice(0, max).trimEnd()}...`;
}

/**
 * @param {{ ref?: string, description?: string }} chip
 * @returns {string}
 */
export function dependencyChipLabel(chip = {}) {
  const ref = chip.ref != null ? String(chip.ref).trim() : '';
  const description = chip.description != null
    ? String(chip.description).trim()
    : 'Task';
  return ref ? `${ref} · ${description}` : description;
}

/**
 * Selected picker rows first (chip order), then unselected rows.
 * @param {object[]} records
 * @param {Array<{ taskId?: string }>} chips
 * @returns {object[]}
 */
export function sortLinkPickerRecords(records = [], chips = []) {
  const selectedOrder = new Map();
  (chips || []).forEach((chip, index) => {
    const id = templateIdString(chip.taskId);
    if (id) selectedOrder.set(id, index);
  });

  const selected = [];
  const unselected = [];

  (records || []).forEach((row) => {
    const id = pickerRowId(row);
    if (selectedOrder.has(id)) {
      selected.push(row);
    } else {
      unselected.push(row);
    }
  });

  selected.sort((a, b) => {
    const ai = selectedOrder.get(pickerRowId(a)) ?? 0;
    const bi = selectedOrder.get(pickerRowId(b)) ?? 0;
    return ai - bi;
  });

  return [...selected, ...unselected];
}

/**
 * @param {*} value
 * @returns {boolean}
 */
function hasPickerDisplayValue(value) {
  return value != null && value !== '';
}

/**
 * Row shown for a selected chip when the latest API page leaves it out.
 * @param {{ taskId?: string, ref?: string, description?: string, duedays?: *, duedate?: *, locationName?: string, link?: object }} chip
 * @returns {object}
 */
export function pickerRecordFromChip(chip = {}) {
  return {
    _id: templateIdString(chip.taskId),
    ref: chip.ref,
    description: chip.description,
    duedays: chip.duedays,
    duedate: chip.duedate,
    locationName: chip.locationName || '',
    link: chip.link && typeof chip.link === 'object' ? chip.link : {},
  };
}

/**
 * Fill blank due and location on an API row from the selected chip.
 * @param {object} row
 * @param {object} chip
 * @returns {object}
 */
export function fillPickerRowFromChip(row = {}, chip = {}) {
  const next = { ...row };
  DEPENDENCY_PICKER_DISPLAY_KEYS.forEach((key) => {
    if (!hasPickerDisplayValue(next[key]) && hasPickerDisplayValue(chip[key])) {
      next[key] = chip[key];
    }
  });
  return next;
}

/**
 * Copy due and location from the current picker rows onto selected chips
 * before a reload replaces those rows.
 * @param {object[]} chips
 * @param {object[]} records
 * @returns {object[]}
 */
export function rememberPickerChipDetails(chips = [], records = []) {
  const byId = new Map();
  (records || []).forEach((row) => {
    const id = pickerRowId(row);
    if (id) byId.set(id, row);
  });

  return (chips || []).map((chip) => {
    const row = byId.get(templateIdString(chip.taskId));
    if (!row) return chip;
    const next = { ...chip };
    let changed = false;
    DEPENDENCY_PICKER_DISPLAY_KEYS.forEach((key) => {
      if (!hasPickerDisplayValue(next[key]) && hasPickerDisplayValue(row[key])) {
        next[key] = row[key];
        changed = true;
      }
    });
    return changed ? next : chip;
  });
}

/**
 * Keep linked chips visible in the picker even when the API page omits them.
 * While a search term is active, only API matches are shown (`includeMissingChips: false`).
 * @param {object[]} records
 * @param {Array<{ taskId?: string, ref?: string, description?: string, duedays?: *, duedate?: *, locationName?: string }>} chips
 * @param {{ includeMissingChips?: boolean, sortSelectedFirst?: boolean }} [options]
 * @returns {object[]}
 */
export function mergeLinkedPickerRecords(records = [], chips = [], options = {}) {
  const seen = new Set();
  const merged = [];
  const includeMissingChips = options.includeMissingChips !== false;
  const chipsById = new Map();

  (chips || []).forEach((chip) => {
    const id = templateIdString(chip.taskId);
    if (id) chipsById.set(id, chip);
  });

  (records || []).forEach((row) => {
    const id = pickerRowId(row);
    const chip = id ? chipsById.get(id) : null;
    if (id) seen.add(id);
    merged.push(chip ? fillPickerRowFromChip(row, chip) : row);
  });

  if (includeMissingChips) {
    (chips || []).forEach((chip) => {
      const id = templateIdString(chip.taskId);
      if (!id || seen.has(id)) return;
      seen.add(id);
      merged.push(pickerRecordFromChip(chip));
    });
  }

  if (options.sortSelectedFirst === false) return merged;
  return sortLinkPickerRecords(merged, chips);
}

/**
 * Keep tasks this one is waiting on visible and first in the picker.
 * Those edges are not outbound chips, so a normal page can omit them.
 * @param {object[]} records
 * @param {object[]} waitingOnRows
 * @returns {object[]}
 */
export function pinWaitingOnPickerRecords(records = [], waitingOnRows = []) {
  const pinIds = new Set();
  const extras = [];
  const seen = new Set((records || []).map((row) => pickerRowId(row)).filter(Boolean));

  (waitingOnRows || []).forEach((row) => {
    const id = pickerRowId(row);
    if (!id) return;
    pinIds.add(id);
    if (seen.has(id)) return;
    seen.add(id);
    extras.push({
      _id: id,
      ref: row.ref,
      description: row.description,
      locationName: row.locationName || '',
      duedate: row.duedate,
      stage: row.stage,
      status: row.status,
      link: {},
    });
  });

  const pinned = [];
  const rest = [];
  extras.concat(records || []).forEach((row) => {
    if (pinIds.has(pickerRowId(row))) pinned.push(row);
    else rest.push(row);
  });
  return pinned.concat(rest);
}

/**
 * @param {object} row
 * @returns {boolean}
 */
export function isTaskBlockable(row = {}) {
  const link = row.link || {};
  if (link.blockable != null) return Boolean(link.blockable);
  if (row.blockable != null) return Boolean(row.blockable);
  return false;
}

/**
 * @param {object} row
 * @returns {boolean}
 */
export function isTaskRelatable(row = {}) {
  const link = row.link || {};
  if (link.relatable != null) return Boolean(link.relatable);
  if (row.relatable != null) return Boolean(row.relatable);
  return false;
}

/**
 * @param {object} row
 * @returns {Array<{ code?: string, message?: string }>}
 */
export function collectBlocksLinkReasons(row = {}) {
  const link = row.link || {};
  return Array.isArray(link.blocks?.reasons) ? link.blocks.reasons : [];
}

/**
 * @param {{ code?: string, message?: string }} reason
 * @returns {string}
 */
function linkPickerDisabledReasonMessage(
  reason = {},
  reasonMessages = SETUP_TEMPLATE_DEPENDENCY.PICKER_DISABLED_REASON_MESSAGES,
) {
  const code = reason?.code;
  const mapped = code ? reasonMessages[code] : '';
  return mapped || reason?.message || SETUP_TEMPLATE_DEPENDENCY.PICKER_DISABLED_TOOLTIP_FALLBACK;
}

/**
 * Blocks-only hint beside the link-type control (DUE_ORDER, MISSING_DUE, CIRCULAR).
 * @param {object} row
 * @param {Record<string, string>} [reasonMessages]
 * @returns {string}
 */
export function blocksLinkPickerHint(row = {}, reasonMessages = {}) {
  if (isTaskBlockable(row)) return '';
  const reasons = collectBlocksLinkReasons(row);
  if (!reasons.length) return '';
  return linkPickerDisabledReasonMessage(reasons[0], reasonMessages);
}

/**
 * Extended tooltip copy for disabled picker rows (Figma Tooltip Extended).
 * Shown only when the task cannot be linked as Blocks or Related.
 * @param {object} row
 * @returns {{ title: string, body: string }|null}
 */
export function linkPickerDisabledTooltip(row = {}) {
  if (!isLinkPickerRowDisabled(row)) return null;
  return {
    title: SETUP_TEMPLATE_DEPENDENCY.PICKER_DISABLED_TOOLTIP_TITLE,
    body: SETUP_TEMPLATE_DEPENDENCY.PICKER_DISABLED_TOOLTIP_FALLBACK,
  };
}

/**
 * Row cannot be selected when it is neither blockable nor relatable.
 * @param {object} row
 * @returns {boolean}
 */
export function isLinkPickerRowDisabled(row = {}) {
  return !isTaskRelatable(row) && !isTaskBlockable(row);
}

/**
 * Default link type when a picker row is checked. Prefers Blocks when blockable.
 * @param {object} row
 * @returns {string}
 */
export function defaultPickerLinkType(row = {}) {
  if (isTaskBlockable(row)) {
    return SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS;
  }
  return SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED;
}

/**
 * Picker checkbox disabled when the row is neither blockable nor relatable.
 * @param {object} row
 * @returns {boolean}
 */
export function isPickerRowCheckboxDisabled(row = {}) {
  return isLinkPickerRowDisabled(row);
}

/**
 * Link-type menu items for a picker row.
 * @param {object} row
 * @param {Array<{ label?: string, value?: string }>} [linkTypeOptions]
 * @returns {Array<{ label?: string, value?: string, disabled: boolean }>}
 */
export function linkTypeItemsForPickerRow(row = {}, linkTypeOptions = []) {
  return (linkTypeOptions || []).map((opt) => {
    const value = opt?.value;
    const disabled = value === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS
      ? !isTaskBlockable(row)
      : value === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED
        ? !isTaskRelatable(row)
        : false;
    return {
      label: opt.label,
      value,
      disabled,
    };
  });
}

/**
 * Picker checkbox interaction — already-linked rows stay editable (check/uncheck).
 * @param {object} row
 * @param {Array<{ value?: string }>} linkTypeOptions
 * @param {boolean} isLinked
 * @returns {boolean}
 */
export function isPickerRowInteractionDisabled(row = {}, isLinked = false) {
  if (isLinked) return false;
  return isPickerRowCheckboxDisabled(row);
}

/**
 * Greyed picker row — already-linked rows stay visually active so they can be unchecked.
 * @param {object} row
 * @param {Array<{ value?: string }>} linkTypeOptions
 * @param {boolean} isLinked
 * @returns {boolean}
 */
export function isPickerRowVisuallyDisabled(row = {}, isLinked = false) {
  if (isLinked) return false;
  return isPickerRowCheckboxDisabled(row);
}

/**
 * @param {object} row
 * @param {Array<{ value?: string }>} [linkTypeOptions]
 * @returns {boolean}
 */
export function isSetupTemplatePickerRowDisabled(row = {}) {
  return isPickerRowCheckboxDisabled(row);
}

/**
 * Seed editor chips from getdetails neighbor arrays.
 * @param {{ blocking?: object[], blockedBy?: object[], relates?: object[] }} source
 * @returns {Array<{ taskId: string, ref?: string, description?: string, linkType: string }>}
 */
export function chipsFromDetailArrays(source = {}) {
  const chips = [];
  const push = (rows, linkType) => {
    (rows || []).forEach((row) => {
      const taskId = pickerRowId(row);
      if (!taskId) return;
      chips.push({
        taskId,
        ref: row.ref,
        description: row.description,
        duedays: row.duedays,
        locationName: row.locationName || '',
        linkType,
      });
    });
  };
  push(source.blocking, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS);
  push(source.blockedBy, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKING);
  push(source.relates, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED);
  return chips;
}

/**
 * Seed cycle task editor chips from getdetails neighbor arrays.
 * Only edges owned by this task are editable: blocking → blocks, relates → related.
 * @param {{ blocking?: object[], relates?: object[] }} source
 * @returns {Array<{ taskId: string, ref?: string, description?: string, linkType: string }>}
 */
export function cycleChipsFromDetailArrays(source = {}) {
  const chips = [];
  const seen = new Set();
  const push = (rows, linkType, dueKey) => {
    (rows || []).forEach((row) => {
      const taskId = pickerRowId(row);
      if (!taskId || seen.has(taskId)) return;
      seen.add(taskId);
      chips.push({
        taskId,
        ref: row.ref,
        description: row.description,
        [dueKey]: row[dueKey],
        locationName: row.locationName || '',
        linkType,
      });
    });
  };
  push(source.blocking, CYCLE_TASK_DEPENDENCY.LINK_TYPE_BLOCKS, 'duedate');
  push(source.relates, CYCLE_TASK_DEPENDENCY.LINK_TYPE_RELATED, 'duedate');
  return chips;
}

/**
 * All current neighbors (outbound + inbound waiting-on). Picker rows for these
 * stay enabled even when due-order would block a new Blocks link.
 * @param {{ blocking?: object[], blockedBy?: object[], relates?: object[] }} source
 * @returns {string[]}
 */
export function cycleNeighborIdsFromDetail(source = {}) {
  const ids = [];
  const seen = new Set();
  const push = (rows) => {
    (rows || []).forEach((row) => {
      const taskId = pickerRowId(row);
      if (!taskId || seen.has(taskId)) return;
      seen.add(taskId);
      ids.push(taskId);
    });
  };
  push(source.blocking);
  push(source.blockedBy);
  push(source.relates);
  return ids;
}

/**
 * Task ids this cycle task is waiting on (blockedBy). Not editable outbound links.
 * @param {{ blockedBy?: object[] }} source
 * @returns {string[]}
 */
export function cycleWaitingOnIdsFromDetail(source = {}) {
  const ids = [];
  const seen = new Set();
  (source.blockedBy || []).forEach((row) => {
    const taskId = pickerRowId(row);
    if (!taskId || seen.has(taskId)) return;
    seen.add(taskId);
    ids.push(taskId);
  });
  return ids;
}

/**
 * Drop waiting-on neighbors from editable chips — those edges are owned by the blocker task.
 * @param {Array<{ taskId?: string }>} chips
 * @param {Set<string>|string[]} waitingOnIds
 * @returns {Array<{ taskId?: string, ref?: string, description?: string, linkType: string }>}
 */
export function sanitizeCycleEditableChips(chips = [], waitingOnIds = []) {
  const blocked = waitingOnIds instanceof Set
    ? waitingOnIds
    : new Set((waitingOnIds || []).map((id) => templateIdString(id)).filter(Boolean));

  return (chips || [])
    .filter((chip) => {
      const id = templateIdString(chip.taskId);
      return id && !blocked.has(id);
    })
    .map((chip) => ({ ...chip }));
}

/**
 * @param {Array<{ taskId: string, linkType: string }>} chips
 * @returns {Array<{ taskId: string, linkType: string }>}
 */
export function linksPayloadFromChips(chips) {
  return (chips || [])
    .filter((c) => c.taskId && c.linkType)
    .map((c) => ({
      taskId: templateIdString(c.taskId),
      linkType: String(c.linkType).toLowerCase(),
    }));
}

/**
 * @param {Array<{ linkType?: string }>} chips
 * @returns {boolean}
 */
export function chipsHaveMissingLinkType(chips) {
  return (chips || []).some((c) => c.taskId && !c.linkType);
}

/**
 * ISO calendar date (YYYY-MM-DD) from a stored cycle due date.
 * @param {*} value
 * @returns {string}
 */
function toDueIso(value) {
  if (value == null || value === '') return '';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

/**
 * Earliest due of tasks this one blocks. Cycle returns YYYY-MM-DD, setup returns a number.
 * @param {Array<{ linkType?: string, duedate?: *, duedays?: * }>} chips
 * @param {'cycle'|'setup'} mode
 * @returns {string|number|null}
 */
export function earliestBlockedDueLimit(chips, mode) {
  const isSetup = mode === SETUP_TEMPLATE_DEPENDENCY.MODE;
  const blocksType = isSetup
    ? SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS
    : CYCLE_TASK_DEPENDENCY.LINK_TYPE_BLOCKS;
  let earliest = null;
  (chips || []).forEach((chip) => {
    if (String(chip?.linkType || '').toLowerCase() !== blocksType) return;
    if (isSetup) {
      if (chip.duedays == null || chip.duedays === '') return;
      const days = Number(chip.duedays);
      if (!Number.isFinite(days)) return;
      if (earliest == null || days < earliest) earliest = days;
      return;
    }
    const iso = toDueIso(chip.duedate);
    if (!iso) return;
    if (earliest == null || iso < earliest) earliest = iso;
  });
  return earliest;
}

/**
 * @param {number|null|undefined} duedays
 * @returns {string}
 */
export function formatTemplateDueOffset(duedays) {
  if (duedays === null || duedays === undefined || duedays === '') return '—';
  const n = Number(duedays);
  if (!Number.isFinite(n)) return '—';
  return n < 0 ? `T${n}` : `T+${n}`;
}

/**
 * @param {number|null|undefined} duedays
 * @returns {string}
 */
export function formatTemplateDueDaysLabel(duedays) {
  if (duedays === null || duedays === undefined || duedays === '') return '—';
  const n = Number(duedays);
  if (!Number.isFinite(n)) return '—';
  return `${n} days`;
}

/**
 * @param {string} linkType
 * @returns {string}
 */
export function linkTypeChipLabel(linkType) {
  const t = String(linkType || '').toLowerCase();
  if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKING) {
    return SETUP_TEMPLATE_DEPENDENCY.DETAIL_WAITING_ON;
  }
  if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED) {
    return SETUP_TEMPLATE_DEPENDENCY.DETAIL_RELATED;
  }
  return SETUP_TEMPLATE_DEPENDENCY.DETAIL_BLOCKING;
}

/**
 * @param {string} linkType
 * @returns {'blocking'|'waiting'|'related'}
 */
export function linkTypeChipVariant(linkType) {
  const t = String(linkType || '').toLowerCase();
  if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKING) return 'waiting';
  if (t === SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED) return 'related';
  return 'blocking';
}

/**
 * Flat dependency rows for setup task detail table.
 * @param {{ blocking?: object[], blockedBy?: object[], relates?: object[] }} source
 * @returns {Array<object>}
 */
export function detailRowsFromDependencyArrays(source = {}) {
  const rows = [];
  const push = (list, linkType) => {
    (list || []).forEach((row) => {
      rows.push({
        ...row,
        linkType,
        chipLabel: linkTypeChipLabel(linkType),
        chipVariant: linkTypeChipVariant(linkType),
      });
    });
  };

  push(source.blocking, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKS);
  push(source.blockedBy, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_BLOCKING);
  push(source.relates, SETUP_TEMPLATE_DEPENDENCY.LINK_TYPE_RELATED);

  return rows.sort((a, b) =>
    String(a.ref || '').localeCompare(String(b.ref || ''), undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  );
}
